/**
 * 3D Room Visualizer — Dimension Labels
 *
 * Creates dimension lines and text labels as Three.js sprites.
 */

import * as THREE from 'three';

const LINE_COLOR = 0x0066cc;
const CAP_SIZE = 0.3;

/**
 * Format a dimension value for display.
 */
export function formatDimension(value: number): string {
  if (value % 1 === 0) {
    return `${value}'`;
  }
  return `${value.toFixed(1)}'`;
}

/**
 * Create a text sprite (billboard) for a dimension label.
 */
function createLabelSprite(text: string, x: number, y: number, z: number): THREE.Sprite {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not get canvas 2d context');

  canvas.width = 256;
  canvas.height = 128;

  context.fillStyle = '#0066cc';
  context.font = 'bold 48px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, 128, 64);

  const texture = new THREE.CanvasTexture(canvas);
  const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
  const sprite = new THREE.Sprite(spriteMaterial);
  sprite.position.set(x, y, z);
  sprite.scale.set(2, 1, 1);

  return sprite;
}

/**
 * Create a dimension line with end caps between two points.
 */
function createDimensionLine(
  start: THREE.Vector3,
  end: THREE.Vector3,
  capAxis: 'x' | 'y' | 'z',
  lineMaterial: THREE.LineBasicMaterial
): THREE.Object3D[] {
  const objects: THREE.Object3D[] = [];

  // Main line
  const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
  objects.push(new THREE.Line(geometry, lineMaterial));

  // End caps
  for (const point of [start, end]) {
    const capStart = point.clone();
    const capEnd = point.clone();
    (capStart as any)[capAxis] -= CAP_SIZE;
    (capEnd as any)[capAxis] += CAP_SIZE;

    const capGeometry = new THREE.BufferGeometry().setFromPoints([capStart, capEnd]);
    objects.push(new THREE.Line(capGeometry, lineMaterial));
  }

  return objects;
}

/**
 * Add all dimension lines and labels to a room group.
 */
export function addDimensionLines(
  group: THREE.Group,
  roomLength: number,
  roomWidth: number,
  roomHeight: number,
  showHeight: boolean,
  lengthValue: number,
  widthValue: number,
  heightValue: number
): void {
  const lineMaterial = new THREE.LineBasicMaterial({ color: LINE_COLOR, linewidth: 2 });

  // Length dimension line (front, bottom) - parallel to X axis
  const lengthOffset = -0.8;
  const lengthObjects = createDimensionLine(
    new THREE.Vector3(0, lengthOffset, -0.5),
    new THREE.Vector3(roomLength, lengthOffset, -0.5),
    'y',
    lineMaterial
  );
  lengthObjects.forEach(obj => group.add(obj));
  group.add(createLabelSprite(formatDimension(lengthValue), roomLength / 2, lengthOffset - 1, -0.5));

  // Width dimension line (left/front side, bottom) - visible from default camera angle (y=4.0)
  const widthOffset = -0.8;
  const widthObjects = createDimensionLine(
    new THREE.Vector3(-0.5, widthOffset, 0),
    new THREE.Vector3(-0.5, widthOffset, roomWidth),
    'y',
    lineMaterial
  );
  widthObjects.forEach(obj => group.add(obj));
  group.add(createLabelSprite(formatDimension(widthValue), -0.5, widthOffset - 1, roomWidth / 2));

  // Height dimension line (left front corner) - visible from default camera angle (y=4.0)
  if (showHeight) {
    const heightOffset = 0.8;
    const heightObjects = createDimensionLine(
      new THREE.Vector3(-heightOffset, 0, -0.5),
      new THREE.Vector3(-heightOffset, roomHeight, -0.5),
      'x',
      lineMaterial
    );
    heightObjects.forEach(obj => group.add(obj));
    group.add(createLabelSprite(formatDimension(heightValue), -heightOffset - 1, roomHeight / 2, -0.5));
  }
}
